// The BOOKING calendar (ART-331) — the WRAPPER layer. One artist (`GET /bands/{bandId}/booking/calendar`, the org
// lens mirrors it) or the whole roster (`GET /orgs/{orgId}/roster/booking-calendar`). Booking lens only.
import { useQuery } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandQuery } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

export type BookingCalendarRead = components["schemas"]["BookingCalendarRead"];
export type BookingCalendarItem = components["schemas"]["BookingCalendarItem"];

/** One artist's deals in the window (the mounted band). */
export function useArtistBookingCalendar(from: string, to: string, enabled = true) {
  const { api } = useApp();
  return useBandQuery<BookingCalendarRead>(
    ["booking", "calendar", from, to],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/booking/calendar", {
        params: { path: { bandId }, query: { from, to } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000, enabled },
  );
}

/** Every artist the caller reaches through the org. */
export function useRosterBookingCalendar(orgId: string | undefined, from: string, to: string, enabled = true) {
  const { api } = useApp();
  return useQuery<BookingCalendarRead, Error>({
    queryKey: ["org", orgId ?? "none", "roster", "booking-calendar", from, to],
    enabled: enabled && !!orgId,
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/orgs/{orgId}/roster/booking-calendar", {
        params: { path: { orgId: orgId as string }, query: { from, to } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  });
}
